/**
 * Entrar no site do Cabal Next (dono, 2026-10-17): botao "Entrar" do header, ao lado do "Inscrever Beta", abre este
 * modal no MESMO tema do BetaSignup (classes nxb-, beta.css). Logou -> /account.
 * Embaixo do "Entrar" (dono, 2026-10-24): "ou" + "Inscrever Beta" -> fecha este e abre o BetaSignup (onSignup).
 *   login        ID ou e-mail + senha -> POST /Account/loginVerify
 *   forgot       e-mail -> POST /Account/forgotPassword sem Code (a API manda o CODIGO pelo e-mail, Cloudflare)
 *   forgot-code  codigo -> forgotPassword com Code (CODE_OK)
 *   forgot-pass  senha nova -> forgotPassword com Code + newPassword (PASSWORD_CHANGED)
 *   forgot-done  pronto -> volta pro login com o e-mail preenchido
 * Portal no <body> (o header tem backdrop-filter, que prenderia o position:fixed).
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, MailCheck, X } from 'lucide-react';
import { apiLoginVerify, forgotPassword } from '@/shared/services/authServices';
import { useAuth } from '@/shared/contexts/AuthContext';
import { OtpInput } from '@/features/Cadastro/CadastroParts';
import './beta.css';

type Step = 'login' | 'forgot' | 'forgot-code' | 'forgot-pass' | 'forgot-done';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// O erro do servidor chega como {detail: {Status, Message}}
const errMsg = (e: any, fallback: string) => {
  const b = e?.detail && typeof e.detail === 'object' ? e.detail : e;
  return String(b?.Message || b?.message || (typeof e?.detail === 'string' ? e.detail : '') || e?.message || fallback);
};
const statusOf = (res: any) => String(res?.Status ?? res?.status ?? '');

type Props = { open: boolean; onClose: () => void; onSignup?: () => void };

export function NextLogin({ open, onClose, onSignup }: Props) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('login');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [dest, setDest] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  const go = (next: Step) => {
    setError(null);
    setStep(next);
  };

  // fechar volta pro login (a senha digitada nao fica guardada)
  const close = () => {
    if (loading) return;
    setPassword('');
    setOtp('');
    setNewPass('');
    setConfirmPass('');
    go('login');
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    requestAnimationFrame(() => firstField.current?.focus());
    return () => {
      body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // ---------- login ----------
  const submitLogin = async (e: FormEvent) => {
    e.preventDefault();
    const id = login.trim();
    if (id.length < 3) return setError('Informe seu ID ou e-mail.');
    if (!password) return setError('Informe sua senha.');
    setLoading(true);
    setError(null);
    try {
      const res = await apiLoginVerify({ Username: id, Password: password, Cellphone: null, Code: null });
      if (!res?.token) throw res;
      auth.login({ username: String(res?.username ?? id), token: res.token, expiration: res.expiration || undefined });
      setPassword('');
      setLoading(false);
      onClose();
      // dono (2026-10-20): depois de entrar volta sempre pra home (o "Painel" ja' aparece no menu)
      navigate('/');
    } catch (err: any) {
      setError(errMsg(err, 'ID ou senha incorretos.'));
      setLoading(false);
    }
  };

  // ---------- esqueci a senha ----------
  const sendCode = async () => {
    const mail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(mail)) return setError('Informe um e-mail válido.');
    setLoading(true);
    setError(null);
    try {
      const res = await forgotPassword({ contact: mail });
      if (statusOf(res) !== 'CODE_SENT') throw res;
      setDest(String(res?.Destination ?? res?.destination ?? mail));
      setCooldown(Number(res?.RetryAfterSeconds ?? res?.retryAfterSeconds ?? 60));
      setOtp('');
      go('forgot-code');
    } catch (err: any) {
      setError(errMsg(err, 'Não foi possível enviar o código.'));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = (e: FormEvent) => {
    e.preventDefault();
    void sendCode();
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return setError('Digite o código completo (6 dígitos).');
    setLoading(true);
    setError(null);
    try {
      const res = await forgotPassword({ contact: email.trim().toLowerCase(), code: otp });
      if (statusOf(res) !== 'CODE_OK') throw res;
      go('forgot-pass');
    } catch (err: any) {
      setError(errMsg(err, 'Código inválido ou expirado.'));
    } finally {
      setLoading(false);
    }
  };

  const submitNewPass = async (e: FormEvent) => {
    e.preventDefault();
    if (newPass.length < 6) return setError('A senha deve ter no mínimo 6 caracteres.');
    if (newPass !== confirmPass) return setError('As senhas não conferem.');
    setLoading(true);
    setError(null);
    try {
      const res = await forgotPassword({ contact: email.trim().toLowerCase(), code: otp, newPassword: newPass });
      if (statusOf(res) !== 'PASSWORD_CHANGED') throw res;
      setNewPass('');
      setConfirmPass('');
      setOtp('');
      setLogin(email.trim().toLowerCase());
      go('forgot-done');
    } catch (err: any) {
      setError(errMsg(err, 'Não foi possível alterar a senha.'));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="nxb-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="nxb-modal nxb-modal--login" role="dialog" aria-modal="true" aria-labelledby="nxl-title">
        <button type="button" className="nxb-close" onClick={close} aria-label="Fechar" disabled={loading}>
          <X size={20} />
        </button>

        {step === 'login' && (
          <form onSubmit={submitLogin} noValidate>
            <p className="nxb-eyebrow">Cabal Next</p>
            <h2 id="nxl-title" className="nxb-title">
              Entrar na <span>conta</span>
            </h2>
            <p className="nxb-lead">Use o mesmo ID (ou e-mail) e senha do jogo.</p>
            <label className="nxb-field">
              <span>ID ou e-mail</span>
              <input
                ref={firstField}
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Seu ID ou e-mail"
                autoComplete="username"
              />
            </label>
            <label className="nxb-field">
              <span>Senha</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                autoComplete="current-password"
              />
            </label>
            <div className="nxb-links nxb-links--end">
              <button
                type="button"
                onClick={() => {
                  if (EMAIL_RE.test(login.trim())) setEmail(login.trim());
                  go('forgot');
                }}
                disabled={loading}
              >
                Esqueci minha senha
              </button>
            </div>
            {error && <p className="nxb-error">{error}</p>}
            <button type="submit" className="nxb-submit" disabled={loading}>
              {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
            {onSignup && (
              <>
                <div className="nxb-or">
                  <span>ou</span>
                </div>
                <button
                  type="button"
                  className="nxb-signup-link"
                  onClick={() => {
                    close();
                    onSignup();
                  }}
                  disabled={loading}
                >
                  Inscrever Beta
                </button>
              </>
            )}
          </form>
        )}

        {step === 'forgot' && (
          <form onSubmit={submitForgot} noValidate>
            <span className="nxb-badge">
              <KeyRound size={28} />
            </span>
            <h2 id="nxl-title" className="nxb-title nxb-title--center">
              Esqueci a <span>senha</span>
            </h2>
            <p className="nxb-lead nxb-lead--center">
              Informe o e-mail da sua conta. Vamos enviar um código para você criar uma senha nova.
            </p>
            <label className="nxb-field">
              <span>E-mail</span>
              <input
                ref={firstField}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@exemplo.com"
                autoComplete="email"
              />
            </label>
            {error && <p className="nxb-error">{error}</p>}
            <button type="submit" className="nxb-submit" disabled={loading}>
              {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
              {loading ? 'Enviando código...' : 'Enviar código'}
            </button>
            <button type="button" className="nxb-ghost" onClick={() => go('login')} disabled={loading}>
              Voltar para o login
            </button>
          </form>
        )}

        {step === 'forgot-code' && (
          <form onSubmit={submitCode} noValidate>
            <span className="nxb-badge">
              <MailCheck size={30} />
            </span>
            <h2 id="nxl-title" className="nxb-title nxb-title--center">
              Confirme o <span>código</span>
            </h2>
            <p className="nxb-lead nxb-lead--center">
              Enviamos um código de 6 dígitos para <b>{dest}</b>.
              <small>Não chegou? Confira também a caixa de spam.</small>
            </p>
            <div className="nxb-otp">
              <OtpInput value={otp} onChange={setOtp} disabled={loading} length={6} />
            </div>
            <div className="nxb-links">
              <button type="button" onClick={() => go('forgot')} disabled={loading}>
                <ArrowLeft size={14} /> Trocar e-mail
              </button>
              <button type="button" onClick={() => void sendCode()} disabled={loading || cooldown > 0}>
                {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
              </button>
            </div>
            {error && <p className="nxb-error">{error}</p>}
            <button type="submit" className="nxb-submit" disabled={loading || otp.length !== 6}>
              {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
              {loading ? 'Validando...' : 'Confirmar código'}
            </button>
          </form>
        )}

        {step === 'forgot-pass' && (
          <form onSubmit={submitNewPass} noValidate>
            <span className="nxb-badge">
              <KeyRound size={28} />
            </span>
            <h2 id="nxl-title" className="nxb-title nxb-title--center">
              Nova <span>senha</span>
            </h2>
            <p className="nxb-lead nxb-lead--center">Escolha a senha nova da sua conta (vale para o site e o jogo).</p>
            <label className="nxb-field">
              <span>Nova senha</span>
              <input
                ref={firstField}
                type="password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password"
              />
            </label>
            <label className="nxb-field">
              <span>Confirmar senha</span>
              <input
                type="password"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                placeholder="Repita a senha"
                autoComplete="new-password"
              />
            </label>
            {error && <p className="nxb-error">{error}</p>}
            <button type="submit" className="nxb-submit" disabled={loading}>
              {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
              {loading ? 'Salvando...' : 'Salvar senha'}
            </button>
          </form>
        )}

        {step === 'forgot-done' && (
          <div className="nxb-done">
            <span className="nxb-badge">
              <CheckCircle2 size={30} />
            </span>
            <h2 id="nxl-title" className="nxb-title nxb-title--center">
              Senha <span>alterada!</span>
            </h2>
            <p className="nxb-lead nxb-lead--center">Pronto: agora é só entrar com a senha nova.</p>
            <button type="button" className="nxb-submit" onClick={() => go('login')}>
              Entrar
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
